import React, { useLayoutEffect, type ReactNode } from 'react';
import Spinner from '@atlaskit/spinner';
import type { ForgeDoc } from '@atlassian/forge-ui-types';
import type { InitialRenderPayload } from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';

type FireEventOnUnmountProps = {
	forgeDoc?: ForgeDoc;
	appTime?: number;
	error?: string;
	onInitialRender: (payload: InitialRenderPayload) => void;
};

type Props = FireEventOnUnmountProps & {
	loadingComponent?: ReactNode;
	onUnmount?: () => void;
};

const FireEventOnUnmount = ({
	onInitialRender,
	appTime,
	forgeDoc,
	error,
}: FireEventOnUnmountProps) => {
	useLayoutEffect(
		// immediately returning a function here defines
		// a clean-up function that gets called during unmount
		() => () => {
			// don't want to fire yet if forgeDoc and error are both undefined (initial values)
			if (forgeDoc === undefined && error === undefined) {
				return;
			}
			onInitialRender({ forgeDoc, appTime, error });
		},
		[onInitialRender, forgeDoc, error, appTime],
	);

	return null;
};

const OnLoadingComponentUnmount = ({ onUnmount }: { onUnmount: () => void }) => {
	useLayoutEffect(
		// immediately returning a function here defines
		// a clean-up function that gets called during unmount
		() => () => {
			onUnmount();
		},
		[onUnmount],
	);

	return null;
};

export const LoadingComponentWithUnmountEvent = ({
	loadingComponent,
	onInitialRender,
	forgeDoc,
	appTime = 0,
	error,
	onUnmount,
}: Props): React.JSX.Element => (
	<>
		{loadingComponent ?? <Spinner />}
		{onUnmount ? (
			<OnLoadingComponentUnmount onUnmount={onUnmount} />
		) : (
			<FireEventOnUnmount
				onInitialRender={onInitialRender}
				forgeDoc={forgeDoc}
				appTime={appTime}
				error={error}
			/>
		)}
	</>
);
